import React, { useState, useEffect } from 'react';
import { Calculator, Users, MousePointer, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const LandingPageConversionRateCalculator = () => {
  const [visitors, setVisitors] = useState(1000);
  const [conversions, setConversions] = useState(50);
  const [conversionRate, setConversionRate] = useState(0);

  useEffect(() => {
    calculateConversionRate();
  }, [visitors, conversions]);

  const calculateConversionRate = () => {
    const rate = (conversions / visitors) * 100;
    setConversionRate(parseFloat(rate.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/landing-page-conversion-rate-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Landing Page Conversion Rate Calculator: Boost Conversions | Free Tool</title>
        <meta name="description" content="Calculate your Landing Page Conversion Rate instantly. Compare with industry benchmarks, get optimization tips, and improve your conversion rates." />
        <meta name="keywords" content="landing page conversion rate calculator, conversion optimization, CRO metrics, landing page analytics, conversion tracking" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Landing Page Conversion Calculator: Optimize Your Pages" />
        <meta property="og:description" content="Calculate your Landing Page Conversion Rate instantly. Get actionable insights to improve conversions and boost ROI." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Landing Page Conversion Calculator: Optimize Your Pages" />
        <meta property="twitter:description" content="Calculate your Landing Page Conversion Rate instantly. Get actionable insights to improve conversions and boost ROI." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Landing Page Conversion Rate Calculator",
            "description": "Free calculator to measure and optimize landing page conversion rates",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Conversion Rate",
              "Industry Benchmarks",
              "Optimization Tips",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Landing Page Conversion Rate",
              "description": "The percentage of visitors who complete a desired action on a landing page."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Digital Marketers, CRO Specialists, Website Owners"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Landing Page Conversion Rate",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter total number of visitors"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input total number of conversions"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your conversion rate and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Landing Page Conversion Rate Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Visitors</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Users className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={visitors}
                  onChange={(e) => setVisitors(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="1000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Conversions</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MousePointer className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={conversions}
                  onChange={(e) => setConversions(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md bg-gray-50"
                  placeholder="50"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {conversionRate}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Landing Page Conversion Rate</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Landing Page Conversion Rate is {conversionRate}%. This means that {conversionRate}% of visitors are taking the desired action.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Industry Benchmarks</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>SaaS: 3-5%</li>
          <li>E-commerce: 2-3%</li>
          <li>B2B: 2.5-4%</li>
          <li>Lead Generation: 3-6%</li>
          <li>Finance: 2-5%</li>
          <li>Education: 2.5-4%</li>
          <li>Professional Services: 3-5%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Conversion Optimization Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Create compelling headlines</li>
          <li>Use strong visuals</li>
          <li>Craft persuasive copy</li>
          <li>Include social proof</li>
          <li>Optimize CTAs</li>
          <li>Reduce form fields</li>
          <li>Ensure mobile responsiveness</li>
          <li>Implement A/B testing</li>
          <li>Improve page speed</li>
          <li>Add trust signals</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Conversion Elements Impact</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Strong headline: +20-30%</li>
          <li>Social proof: +15-25%</li>
          <li>Clear CTA: +25-35%</li>
          <li>Form optimization: +15-20%</li>
          <li>Trust badges: +10-15%</li>
          <li>Mobile optimization: +20-30%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Testing Priorities</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Headline variations</li>
          <li>CTA button design</li>
          <li>Form length</li>
          <li>Page layout</li>
          <li>Value proposition</li>
          <li>Social proof placement</li>
        </ul>
      </div>
    </div>
  );
};

export default LandingPageConversionRateCalculator;
